import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, cn } from '@/lib/utils';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { SkeletonCard } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { PRODUCT_CATEGORY_LABELS } from '@golf-booking/shared';

const categories = [
  { value: '', label: 'All' },
  { value: 'clubs', label: 'Clubs' },
  { value: 'balls', label: 'Balls' },
  { value: 'apparel', label: 'Apparel' },
  { value: 'shoes', label: 'Shoes' },
  { value: 'bags', label: 'Bags' },
  { value: 'accessories', label: 'Accessories' },
  { value: 'gloves', label: 'Gloves' },
  { value: 'rentals', label: 'Rentals' },
];

export function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || '';
  const [page, setPage] = useState(1);

  const { data: products, isLoading } = useQuery({
    queryKey: ['products', category, page],
    queryFn: () => api.products.list({
      category: category || undefined,
      page,
      pageSize: 12,
    }),
  });

  const handleCategoryChange = (newCategory: string) => {
    setPage(1);
    if (newCategory) {
      setSearchParams({ category: newCategory });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="bg-sand-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-sand-100">
        <div className="container-wide py-8">
          <h1 className="text-3xl font-bold text-sand-900">Pro Shop</h1>
          <p className="mt-2 text-sand-500">Premium gear and accessories for every golfer</p>
        </div>
      </div>

      {/* Categories */}
      <div className="sticky top-16 z-40 bg-white border-b border-sand-100 shadow-soft">
        <div className="container-wide py-4">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => handleCategoryChange(cat.value)}
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors',
                  category === cat.value
                    ? 'bg-sand-900 text-white'
                    : 'bg-sand-100 text-sand-700 hover:bg-sand-200'
                )}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="container-wide py-8">
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : products?.data?.length === 0 ? (
          <EmptyState
            icon={
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            }
            title="No products found"
            description="Try selecting a different category."
          />
        ) : (
          <>
            <p className="mb-6 text-sm text-sand-500">
              {products?.pagination?.totalCount} product{products?.pagination?.totalCount !== 1 ? 's' : ''}
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {products?.data?.map((product) => (
                <Link key={product.id} to={`/shop/${product.id}`}>
                  <Card variant="hover" padding="none" className="overflow-hidden h-full">
                    <div className="aspect-square bg-sand-100 relative">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <svg className="h-16 w-16 text-sand-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      {product.isFeatured && (
                        <Badge variant="sage" className="absolute top-3 left-3">
                          Featured
                        </Badge>
                      )}
                      {product.isRental && (
                        <Badge variant="terracotta" className="absolute top-3 right-3">
                          Rental
                        </Badge>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="text-sm text-sand-500">
                        {PRODUCT_CATEGORY_LABELS[product.category] || product.category}
                      </p>
                      <p className="mt-1 font-medium text-sand-900 line-clamp-2">{product.name}</p>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-lg font-semibold text-sage-600">
                          {formatCurrency(product.price)}
                        </span>
                        {product.compareAtPrice && (
                          <span className="text-sm text-sand-400 line-through">
                            {formatCurrency(product.compareAtPrice)}
                          </span>
                        )}
                      </div>
                      {product.quantity <= 5 && product.quantity > 0 && (
                        <p className="mt-2 text-xs text-terracotta-600">Only {product.quantity} left</p>
                      )}
                      {product.quantity === 0 && (
                        <p className="mt-2 text-xs text-sand-400">Out of stock</p>
                      )}
                    </div>
                  </Card>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {products?.pagination && products.pagination.totalPages > 1 && (
              <div className="mt-8 flex justify-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={!products.pagination.hasPreviousPage}
                  className="btn-outline disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="flex items-center px-4 text-sm text-sand-500">
                  Page {page} of {products.pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={!products.pagination.hasNextPage}
                  className="btn-outline disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
